import { useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  ComposedChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { buildThemeClasses } from "../theme";
import FinanceLinkCard from "../../expenses/components/FinanceLinkCard";
import {
  useDeals,
  weightedValue,
  isOpen,
  formatMoney,
  monthKey,
  monthLabel,
  quarterKey,
  STAGES,
  STAGE_BY_ID,
  OWNERS,
  OWNER_BY_ID,
  FORECAST_CATEGORY_META,
  type ForecastCategory,
} from "../services/dealsService";

// ===========================================================================
// Pronóstico — probability-weighted revenue forecast over the shared deals
// store. Rolls open deals into forecast categories (commit / best case /
// pipeline), compares weighted forecast against the team's quota, and breaks
// the number down by owner, stage or period. Includes CSV export. Mirrors the
// "Forecast" surfaces in HubSpot / GoHighLevel.
// ===========================================================================

type GroupBy = "owner" | "stage" | "month" | "quarter";

const GROUP_OPTIONS: { id: GroupBy; label: string }[] = [
  { id: "owner", label: "Propietario" },
  { id: "stage", label: "Etapa" },
  { id: "month", label: "Mes" },
  { id: "quarter", label: "Trimestre" },
];

interface Row {
  key: string;
  label: string;
  count: number;
  openAmount: number;
  weighted: number;
  won: number;
  /** Forecast = won + weighted open. */
  forecast: number;
  quota?: number;
}

const CATEGORY_ORDER: ForecastCategory[] = ["won", "commit", "best_case", "pipeline"];

export default function ForecastView() {
  const tc = buildThemeClasses();
  const deals = useDeals();

  const [groupBy, setGroupBy] = useState<GroupBy>("owner");
  const [ownerFilter, setOwnerFilter] = useState<string | "all">("all");

  const scoped = useMemo(
    () => (ownerFilter === "all" ? deals : deals.filter((d) => d.ownerId === ownerFilter)),
    [deals, ownerFilter],
  );

  // --- Headline numbers -----------------------------------------------------
  const totals = useMemo(() => {
    const open = scoped.filter(isOpen);
    // «Terminado» (#1043) también es ganado: el proyecto se cobró y se entregó.
    const won = scoped.filter((d) => STAGE_BY_ID[d.stage]?.won === true);
    const lost = scoped.filter((d) => d.stage === "perdido");
    const openAmount = open.reduce((s, d) => s + d.amount, 0);
    const weighted = open.reduce((s, d) => s + weightedValue(d), 0);
    const wonAmount = won.reduce((s, d) => s + d.amount, 0);
    const forecast = wonAmount + weighted;
    const quota =
      ownerFilter === "all"
        ? OWNERS.reduce((s, o) => s + o.quota, 0)
        : OWNER_BY_ID[ownerFilter]?.quota ?? 0;
    const closedTotal = wonAmount + lost.reduce((s, d) => s + d.amount, 0);
    const winRate = closedTotal > 0 ? Math.round((wonAmount / closedTotal) * 100) : 0;
    return { openAmount, weighted, wonAmount, forecast, quota, winRate, openCount: open.length, wonCount: won.length };
  }, [scoped, ownerFilter]);

  const attainment = totals.quota > 0 ? Math.round((totals.forecast / totals.quota) * 100) : 0;
  const wonAttainment = totals.quota > 0 ? Math.round((totals.wonAmount / totals.quota) * 100) : 0;

  // --- Forecast by category (for pie + legend) ------------------------------
  const byCategory = useMemo(() => {
    const acc: Record<ForecastCategory, number> = {
      won: 0, commit: 0, best_case: 0, pipeline: 0, frozen: 0, lost: 0,
    };
    for (const d of scoped) {
      const cat = STAGE_BY_ID[d.stage].category;
      if (cat === "lost" || cat === "frozen") continue;
      acc[cat] += cat === "won" ? d.amount : weightedValue(d);
    }
    return CATEGORY_ORDER.map((c) => ({
      key: c,
      label: FORECAST_CATEGORY_META[c].label,
      value: acc[c],
      bar: FORECAST_CATEGORY_META[c].bar,
    })).filter((r) => r.value > 0);
  }, [scoped]);

  // --- Grouped breakdown ----------------------------------------------------
  const rows = useMemo<Row[]>(() => {
    const map = new Map<string, Row>();
    const ensure = (key: string, label: string, quota?: number): Row => {
      let r = map.get(key);
      if (!r) {
        r = { key, label, count: 0, openAmount: 0, weighted: 0, won: 0, forecast: 0, quota };
        map.set(key, r);
      }
      return r;
    };

    for (const d of scoped) {
      let key: string;
      let label: string;
      let quota: number | undefined;
      if (groupBy === "owner") {
        const o = OWNER_BY_ID[d.ownerId];
        key = d.ownerId;
        label = o?.name ?? "Sin asignar";
        quota = o?.quota;
      } else if (groupBy === "stage") {
        key = d.stage;
        label = STAGE_BY_ID[d.stage].label;
      } else if (groupBy === "month") {
        key = monthKey(d.closeDate);
        label = monthLabel(key);
      } else {
        key = quarterKey(d.closeDate);
        label = key.replace("-", " · ");
      }
      const r = ensure(key, label, quota);
      r.count += 1;
      if (isOpen(d)) {
        r.openAmount += d.amount;
        r.weighted += weightedValue(d);
      }
      if (STAGE_BY_ID[d.stage]?.won === true) r.won += d.amount;
      r.forecast = r.won + r.weighted;
    }

    const list = Array.from(map.values());
    if (groupBy === "stage") {
      const order = STAGES.map((s) => s.id) as string[];
      list.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
    } else if (groupBy === "month" || groupBy === "quarter") {
      list.sort((a, b) => a.key.localeCompare(b.key));
    } else {
      list.sort((a, b) => b.forecast - a.forecast);
    }
    return list;
  }, [scoped, groupBy]);

  const maxForecast = Math.max(1, ...rows.map((r) => Math.max(r.forecast, r.quota ?? 0)));

  // --- CSV export -----------------------------------------------------------
  const exportCsv = () => {
    const header = ["Grupo", "Negocios", "Valor abierto", "Ponderado", "Ganado", "Pronostico", "Cuota"];
    const lines = rows.map((r) =>
      [r.label, r.count, r.openAmount, r.weighted, r.won, r.forecast, r.quota ?? ""].join(","),
    );
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pronostico-${groupBy}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const axisTick = { fontSize: 12, fill: "rgb(var(--c-content-muted))" };
  const tooltipStyle = {
    background: "rgb(var(--c-card))",
    border: "none",
    borderRadius: 8,
    fontSize: 12,
  };

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-line-chart-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Pronóstico</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                Proyección de ingresos ponderada por probabilidad vs. cuota del equipo
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={ownerFilter}
              onChange={(e) => setOwnerFilter(e.target.value)}
              className="rounded-lg border px-3 py-2 text-sm outline-none border-line bg-app text-content"
            >
              <option value="all">Todo el equipo</option>
              {OWNERS.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            <button
              onClick={exportCsv}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.text} ${tc.buttonHover}`}
            >
              <i className="ri-download-2-line" /> CSV
            </button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard tc={tc} label="Pronóstico ponderado" value={formatMoney(totals.forecast, true)} icon="ri-scales-3-line" accent
            sub={`${attainment}% de la cuota`} />
          <KpiCard tc={tc} label="Cerrado ganado" value={formatMoney(totals.wonAmount, true)} icon="ri-trophy-line"
            sub={`${totals.wonCount} negocios · ${wonAttainment}% cuota`} />
          <KpiCard tc={tc} label="Pipeline abierto" value={formatMoney(totals.openAmount, true)} icon="ri-funds-line"
            sub={`${totals.openCount} negocios abiertos`} />
          <KpiCard tc={tc} label="Tasa de cierre" value={`${totals.winRate}%`} icon="ri-percent-line"
            sub="Ganados / cerrados" />
        </div>

        {/* Vínculo con Finanzas (#1290): ingreso esperado contra gasto presupuestado. */}
        <FinanceLinkCard />

        {/* Quota attainment bar */}
        <div className={`mt-4 rounded-xl border p-5 ${tc.cardBorder} ${tc.cardBg}`}>
          <div className="mb-2 flex items-center justify-between">
            <h3 className={`text-sm font-bold ${tc.text}`}>Avance de cuota</h3>
            <span className={`text-sm ${tc.textSecondary}`}>
              {formatMoney(totals.forecast, true)} / {formatMoney(totals.quota, true)}
            </span>
          </div>
          <div className="relative h-5 w-full overflow-hidden rounded-full bg-subtle">
            {/* won segment */}
            <div
              className="absolute left-0 top-0 h-full bg-emerald-500"
              style={{ width: `${Math.min(100, wonAttainment)}%` }}
              title="Cerrado ganado"
            />
            {/* weighted forecast on top (lighter) */}
            <div
              className="absolute top-0 h-full bg-primary-500/40"
              style={{ left: `${Math.min(100, wonAttainment)}%`, width: `${Math.min(100 - Math.min(100, wonAttainment), Math.max(0, attainment - wonAttainment))}%` }}
              title="Pronóstico ponderado abierto"
            />
            {/* 100% marker */}
            <div className="absolute top-0 h-full border-r-2 border-dashed border-gray-400" style={{ left: "100%" }} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-4 text-xs">
            <Legendish color="bg-emerald-500" label="Ganado" tc={tc} />
            <Legendish color="bg-primary-500/40" label="Ponderado abierto" tc={tc} />
            <span className={`ml-auto font-semibold ${attainment >= 100 ? "text-emerald-500" : tc.text}`}>
              {attainment}% de avance
            </span>
          </div>
        </div>

        {/* Charts: category pie + monthly composed */}
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className={`rounded-xl border p-5 ${tc.cardBorder} ${tc.cardBg}`}>
            <h3 className={`mb-4 text-sm font-bold ${tc.text}`}>Pronóstico por categoría</h3>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={byCategory} dataKey="value" nameKey="label" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {byCategory.map((entry) => (
                      <Cell key={entry.key} fill={categoryColor(entry.key)} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => formatMoney(v)} contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 space-y-1">
              {byCategory.map((c) => (
                <div key={c.key} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: categoryColor(c.key) }} />
                    <span className={tc.text}>{c.label}</span>
                  </span>
                  <span className={`font-medium ${tc.text}`}>{formatMoney(c.value, true)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-xl border p-5 lg:col-span-2 ${tc.cardBorder} ${tc.cardBg}`}>
            <h3 className={`mb-4 text-sm font-bold ${tc.text}`}>
              {groupBy === "owner" ? "Pronóstico vs. cuota por propietario" : "Pronóstico ponderado"}
            </h3>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <ComposedChart data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={"rgb(var(--c-border))"} />
                  <XAxis dataKey="label" tick={axisTick} interval={0} angle={rows.length > 5 ? -15 : 0} textAnchor={rows.length > 5 ? "end" : "middle"} height={rows.length > 5 ? 50 : 30} />
                  <YAxis tick={axisTick} tickFormatter={(v) => formatMoney(Number(v), true)} width={70} />
                  <Tooltip formatter={(v: number) => formatMoney(v)} contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="won" stackId="f" name="Ganado" fill="#10B981" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="weighted" stackId="f" name="Ponderado" fill="#0084D5" radius={[4, 4, 0, 0]} />
                  {groupBy === "owner" && (
                    <Line type="monotone" dataKey="quota" name="Cuota" stroke="#F59E0B" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Breakdown table */}
        <div className={`mt-6 rounded-xl border ${tc.cardBorder} ${tc.cardBg}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 p-4">
            <h3 className={`text-sm font-bold ${tc.text}`}>Desglose</h3>
            <div className="flex flex-wrap gap-1">
              {GROUP_OPTIONS.map((g) => (
                <button
                  key={g.id}
                  onClick={() => setGroupBy(g.id)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                    groupBy === g.id
                      ? "bg-primary-500 text-white"
                      : "bg-subtle text-content-muted hover:bg-line"
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>
          <div className="w-full overflow-x-auto px-4 pb-4">
            <table className="w-full text-xs">
              <thead>
                <tr className={`border-b ${tc.border} ${tc.textSecondary}`}>
                  <th className="p-2 text-left font-semibold">{GROUP_OPTIONS.find((g) => g.id === groupBy)?.label}</th>
                  <th className="p-2 text-right font-semibold">Negocios</th>
                  <th className="p-2 text-right font-semibold">Valor abierto</th>
                  <th className="p-2 text-right font-semibold">Ponderado</th>
                  <th className="p-2 text-right font-semibold">Ganado</th>
                  <th className="p-2 text-right font-semibold">Pronóstico</th>
                  {groupBy === "owner" && <th className="p-2 text-right font-semibold">Cuota</th>}
                  <th className="p-2 text-left font-semibold">Avance</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const pct = r.quota && r.quota > 0 ? Math.round((r.forecast / r.quota) * 100) : null;
                  return (
                    <tr key={r.key} className={`border-b last:border-0 ${tc.border}`}>
                      <td className={`p-2 font-medium ${tc.text}`}>{r.label}</td>
                      <td className={`p-2 text-right ${tc.textSecondary}`}>{r.count}</td>
                      <td className={`p-2 text-right ${tc.text}`}>{formatMoney(r.openAmount, true)}</td>
                      <td className={`p-2 text-right ${tc.text}`}>{formatMoney(r.weighted, true)}</td>
                      <td className="p-2 text-right text-emerald-500">{formatMoney(r.won, true)}</td>
                      <td className={`p-2 text-right font-bold ${tc.text}`}>{formatMoney(r.forecast, true)}</td>
                      {groupBy === "owner" && <td className={`p-2 text-right ${tc.textSecondary}`}>{formatMoney(r.quota ?? 0, true)}</td>}
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-subtle">
                            <div
                              className={`h-full rounded-full ${pct != null && pct >= 100 ? "bg-emerald-500" : "bg-primary-500"}`}
                              style={{ width: `${Math.min(100, ((r.forecast / maxForecast) * 100))}%` }}
                            />
                          </div>
                          {pct != null && <span className={`text-[11px] ${pct >= 100 ? "text-emerald-500" : tc.textSecondary}`}>{pct}%</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className={`p-6 text-center ${tc.textSecondary}`}>Sin negocios para este filtro.</td>
                  </tr>
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr className={`border-t-2 ${tc.border} font-bold ${tc.text}`}>
                    <td className="p-2">Total</td>
                    <td className="p-2 text-right">{rows.reduce((s, r) => s + r.count, 0)}</td>
                    <td className="p-2 text-right">{formatMoney(rows.reduce((s, r) => s + r.openAmount, 0), true)}</td>
                    <td className="p-2 text-right">{formatMoney(rows.reduce((s, r) => s + r.weighted, 0), true)}</td>
                    <td className="p-2 text-right text-emerald-500">{formatMoney(rows.reduce((s, r) => s + r.won, 0), true)}</td>
                    <td className="p-2 text-right">{formatMoney(rows.reduce((s, r) => s + r.forecast, 0), true)}</td>
                    {groupBy === "owner" && <td className="p-2 text-right">{formatMoney(rows.reduce((s, r) => s + (r.quota ?? 0), 0), true)}</td>}
                    <td className="p-2" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        <p className={`mt-3 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          El pronóstico pondera cada negocio abierto por su probabilidad y suma los ganados. Comparte datos con el Pipeline.
        </p>
      </div>
    </div>
  );
}

// --- Pieces -----------------------------------------------------------------

type TC = ReturnType<typeof buildThemeClasses>;

function categoryColor(cat: ForecastCategory): string {
  switch (cat) {
    case "won": return "#10B981";
    case "commit": return "#F97316";
    case "best_case": return "#F59E0B";
    case "pipeline": return "#0084D5";
    default: return "#9CA3AF";
  }
}

function KpiCard({
  tc, label, value, icon, sub, accent,
}: { tc: TC; label: string; value: string; icon: string; sub?: string; accent?: boolean }) {
  return (
    <div className={`rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className={`text-sm ${tc.textSecondary}`}>{label}</span>
        <i className={`${icon} text-xl ${accent ? "text-primary-500" : tc.textSecondary}`} />
      </div>
      <div className={`text-2xl font-bold ${accent ? "text-primary-500" : tc.text}`}>{value}</div>
      {sub && <div className={`mt-1 text-xs ${tc.textSecondary}`}>{sub}</div>}
    </div>
  );
}

function Legendish({ color, label, tc }: { color: string; label: string; tc: TC }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`h-2.5 w-2.5 rounded-full ${color}`} />
      <span className={tc.textSecondary}>{label}</span>
    </span>
  );
}
